import type { Question, QuestionType, MultiEntryFieldDef } from '@/data/questions';

// ─── Validation interpreter ───────────────────────────────────────────────────

type ValidationRules = {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  patternMessage?: string;
  email?: boolean;
};

const VALIDATION_MESSAGES: Record<string, {
  required: string; email: string; min: (n: number) => string; max: (n: number) => string; format: string;
}> = {
  fr: {
    required: 'Ce champ est requis',
    email: 'Adresse courriel invalide',
    min: (n) => `Minimum ${n} caractères requis`,
    max: (n) => `Maximum ${n} caractères autorisés`,
    format: 'Format invalide',
  },
  en: {
    required: 'This field is required',
    email: 'Invalid email address',
    min: (n) => `Minimum ${n} characters required`,
    max: (n) => `Maximum ${n} characters allowed`,
    format: 'Invalid format',
  },
  es: {
    required: 'Este campo es obligatorio',
    email: 'Correo electrónico no válido',
    min: (n) => `Mínimo ${n} caracteres requeridos`,
    max: (n) => `Máximo ${n} caracteres permitidos`,
    format: 'Formato no válido',
  },
};

function buildValidationFn(
  rules: ValidationRules | null,
  language = 'fr',
): ((value: any) => string | null) | undefined {
  if (!rules) return undefined;
  const m = VALIDATION_MESSAGES[language] ?? VALIDATION_MESSAGES.fr;
  // Normalise: pattern: "email" is a shorthand for the email flag
  const isEmail = rules.email || rules.pattern === 'email';
  const regexPattern = rules.pattern && rules.pattern !== 'email' ? rules.pattern : null;
  return (value: any) => {
    if (rules.required && (value === undefined || value === null || value === '')) {
      return m.required;
    }
    if (isEmail && typeof value === 'string' && value.trim()) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
        return m.email;
      }
    }
    if (rules.minLength && typeof value === 'string' && value.trim().length < rules.minLength) {
      return m.min(rules.minLength);
    }
    if (rules.maxLength && typeof value === 'string' && value.trim().length > rules.maxLength) {
      return m.max(rules.maxLength);
    }
    if (regexPattern && typeof value === 'string' && value.trim()) {
      if (!new RegExp(regexPattern).test(value.trim())) {
        return rules.patternMessage || m.format;
      }
    }
    return null;
  };
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DbFormQuestionRow {
  order_index: number;
  section: string | null;
  depends_on: { questionId: string; value: any } | null;
  label_override: string | null;
  named_label?: any;
  required_override: boolean | null;
  questions: {
    id: string;
    label: string;
    type: string;
    indication: string | null;
    help: string | null;
    example: string | null;
    placeholder: string | null;
    is_required: boolean;
    requires_confirmation: boolean;
    confirmation_text: string | null;
    success_message: string | null;
    why_important_reason: string | null;
    why_important_consequence: string | null;
    options: { code: string; name: string }[] | null;
    document_config: { acceptedFormats: string[]; maxSizeMB: number; maxFiles?: number } | null;
    validation_rules: ValidationRules | null;
    multi_entry_fields: MultiEntryFieldDef[] | null;
    multi_entry_add_label: string | null;
    details_box_label: string | null;
    translations: Record<string, string> | null;
  } | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Normalize options to {code, name} — handles {value, label} from dashboard editor */
function normalizeOptions(
  opts: any[] | null | undefined,
): { code: string; name: string }[] | undefined {
  if (!opts || opts.length === 0) return undefined;
  return opts.map((o: any) => {
    // Une partie du catalogue stocke les choix comme de simples textes
    // (["Oui", "Non"]) au lieu d'objets. Sans ce cas, code et name sortaient
    // vides, et le menu du client s'affichait VIDE : le rendu écarte tout choix
    // sans code ni texte. Le texte sert alors de code et d'étiquette.
    if (typeof o === "string") return { code: o, name: o };
    return {
      code: o.code ?? o.value ?? "",
      name: o.name ?? o.label ?? o.code ?? o.value ?? "",
    };
  });
}

// ─── Mapper ───────────────────────────────────────────────────────────────────

// La traduction d'une liste ne porte que ses TEXTES (libellé, question,
// indication…), rangés par clé de colonne. Elle remplaçait pourtant la
// définition entière : un client anglophone recevait des colonnes sans type ni
// liste de choix, donc des cases de texte là où le français avait un menu ou
// une date. On garde la définition de base et on n'y pose que les textes.
// Les noms d'options se traduisent ailleurs (nameEn / nameEs).
const TRANSLATABLE_COLUMN_TEXTS = [
  'label', 'question', 'indication', 'example', 'help', 'placeholder',
  'whyImportantReason', 'whyImportantConsequence',
] as const;

export function mergeTranslatedColumns(base: any[] | undefined | null, translated: any[] | undefined | null): any[] | undefined {
  if (!Array.isArray(base) || base.length === 0) return Array.isArray(translated) ? translated : undefined;
  if (!Array.isArray(translated) || translated.length === 0) return base;
  const byKey = new Map(translated.filter((f) => f && f.key).map((f) => [f.key, f]));
  return base.map((f) => {
    const t = byKey.get(f.key);
    if (!t) return f;
    const out = { ...f };
    for (const k of TRANSLATABLE_COLUMN_TEXTS) {
      if (typeof t[k] === 'string' && t[k]) out[k] = t[k];
    }
    return out;
  });
}

export function mapDbRowsToQuestions(rows: DbFormQuestionRow[], language = 'fr'): {
  questions: Record<string, Question>;
  order: string[];
} {
  const questions: Record<string, Question> = {};
  const order: string[] = [];

  for (const row of rows) {
    if (!row.questions) continue;
    const q = row.questions;

    // Resolve translated fields. translations can be:
    //   Structured: { "en": { "label": "...", "indication": "...", "help": "...", "options": [...], ... }, ... }
    //   Flat (legacy): { "en": "label text", ... }
    const t = q.translations?.[language] ?? q.translations?.['fr'];
    const isStructured = t && typeof t === 'object' && !Array.isArray(t);
    const tLabel = isStructured ? (t as any).label : (typeof t === 'string' ? t : null);
    const tIndication = isStructured ? (t as any).indication : null;
    const tHelp = isStructured ? (t as any).help : null;
    const tSuccessMessage = isStructured ? (t as any).successMessage : null;
    const tOptions = isStructured ? (t as any).options : null;
    const tMultiEntryFields = isStructured ? (t as any).multiEntryFields : null;
    const tMultiEntryAddLabel = isStructured ? (t as any).multiEntryAddLabel : null;
    const tDetailsBoxLabel = isStructured ? (t as any).detailsBoxLabel : null;
    const tExample = isStructured ? (t as any).example : null;
    const tPlaceholder = isStructured ? (t as any).placeholder : null;
    const tWhyReason = isStructured ? (t as any).whyImportantReason : null;
    const tWhyConseq = isStructured ? (t as any).whyImportantConsequence : null;

    // Resolve section name from section_translations on form_questions row
    const sectionT = (row as any).section_translations;
    const tSection = sectionT?.[language] ?? sectionT?.['fr'] ?? null;

    const baseSection = row.section ?? 'Général';
    const question: Question = {
      id: q.id,
      // Un formulaire peut reformuler une question partagée (le PEQ demande
      // « citoyenneté » là où les IMM disent « nationalité »). Cette
      // reformulation est écrite en français seulement : la faire gagner dans
      // les autres langues affichait une ligne française à un client anglophone.
      // Hors français, la traduction passe donc devant.
      label:
        language !== 'fr' && tLabel
          ? tLabel
          : (row.label_override ?? tLabel ?? q.label),
      translations: q.translations ?? undefined,
      type: (q.type === 'boolean' ? 'yes-no' : q.type) as QuestionType,
      section: tSection ?? baseSection,
      sectionKey: baseSection,
      indication: tIndication ?? q.indication ?? '',
      help: tHelp ?? q.help ?? '',
      example: (tExample ?? q.example) || undefined,
      placeholder: (tPlaceholder ?? q.placeholder) || undefined,
      isRequired: q.type === 'message' ? false : (row.required_override ?? q.is_required ?? true),
      requiresConfirmation: q.requires_confirmation,
      confirmationText: q.confirmation_text || undefined,
      successMessage: tSuccessMessage ?? q.success_message ?? '',
      whyImportant: (tWhyReason ?? q.why_important_reason)
        ? {
            reason: tWhyReason ?? q.why_important_reason,
            consequence: (tWhyConseq ?? q.why_important_consequence) || undefined,
          }
        : undefined,
      options: normalizeOptions(tOptions ?? q.options),
      documentConfig: q.document_config || undefined,
      validation: buildValidationFn(q.validation_rules, language),
      multiEntryFields: mergeTranslatedColumns(q.multi_entry_fields, tMultiEntryFields),
      multiEntryAddLabel: tMultiEntryAddLabel ?? q.multi_entry_add_label ?? undefined,
      detailsBoxLabel: tDetailsBoxLabel ?? q.details_box_label ?? undefined,
      dependsOn: row.depends_on || undefined,
      namedLabel: row.named_label?.list
        ? {
            list: row.named_label.list,
            where: row.named_label.where,
            text: row.named_label[language] ?? row.named_label.fr,
            textNone: row.named_label.none?.[language] ?? row.named_label.none?.fr,
          }
        : undefined,
    };

    questions[q.id] = question;
    order.push(q.id);
  }

  return { questions, order };
}
